import React, {useState} from 'react';
import {useIntl} from 'react-intl';
import {createRefetchContainer, graphql} from 'react-relay';
import {BaseLevelWrapper} from './BaseLevelWrapper';
import {GLOBAL_FILTER_FIELD, GLOBAL_FILTER_OPERATOR, GroupProgressCategories} from '../../../../../constants';
import {GroupRow} from '../rows/GroupRow';
import {useGenericReportContext} from '../../../saved-report/GenericReportContext';

const PhaseRelayWrapperV2 = ({
	relay,
	tableHeader,
	groupings,
	searchQuery,
	viewer,
	rowIndex,
	enabledColumns,
	nextLevelProps,
	currency,
	customFieldColumnCount,
	customFieldKeys,
	pageSize,
}) => {
	const {formatMessage} = useIntl();
	const {reportId, sharedId} = useGenericReportContext();

	const phases = viewer.reportData.phases.edges?.filter(phase => phase.node.taskTotals.entities > 0);
	const hasNextPage = viewer.reportData.phases.pageInfo.hasNextPage && phases?.length >= pageSize;
	const [modalTasksFetching, setModalTasksFetching] = useState(false);

	/**
	 * Grouping filter for task within a phase.
	 *
	 * @param phaseID The phase ID
	 */
	const phaseGroupingFilter = phaseID => {
		return {
			field: GLOBAL_FILTER_FIELD.PROJECT_PHASE,
			operator: GLOBAL_FILTER_OPERATOR.IS,
			value: [phaseID],
		};
	};

	/**
	 * Grouping filter for task without a phase.
	 */
	const noPhaseGroupingFilter = () => {
		return {
			field: GLOBAL_FILTER_FIELD.PHASE_CATEGORY,
			operator: GLOBAL_FILTER_OPERATOR.IS,
			value: GroupProgressCategories.NONE,
		};
	};

	const dataRows = phases.map(phase => {
		return {
			...phase.node,
			generatedName: phase.node.noPhase ? formatMessage({id: 'common.no_phase'}) : phase.node.name,
			currency,
		};
	});

	/**
	 * Extract estimation unit from phase
	 *
	 * @param rowData The row data to extract the estimation unit from
	 */
	const estimationUnit = rowData => rowData.estimationUnit;

	const data = {
		rows: dataRows,
		groupingFilter: rowData => (rowData.noPhase ? noPhaseGroupingFilter() : phaseGroupingFilter(rowData.phaseId)),
		estimationUnit,
	};

	const fetchMore = () => {
		if (!modalTasksFetching) {
			const refetchObject = {};
			setModalTasksFetching(true);
			refetchObject.pageSize = phases.length + pageSize;
			relay.refetch(refetchObject, null, () => setModalTasksFetching(false));
		}
	};

	const phaseRows = () => {
		return GroupRow(
			data,
			rowIndex,
			searchQuery,
			groupings,
			enabledColumns,
			hasNextPage,
			fetchMore,
			nextLevelProps,
			currency,
			reportId,
			sharedId,
			customFieldColumnCount,
			customFieldKeys
		);
	};

	return rowIndex === 1 ? (
		<BaseLevelWrapper
			tableHeader={tableHeader}
			enabledColumns={enabledColumns}
			getRows={phaseRows}
			currentLength={phases.length}
			hasNextPage={hasNextPage}
			refetch={relay.refetch}
			currency={currency}
			pageSize={pageSize}
		/>
	) : (
		phaseRows()
	);
};

const PhaseRelayWrapperV2Query = graphql`
	query PhaseRelayWrapperV2_Query($searchQuery: TaskSearchQueryType, $pageSize: Int, $reportId: String, $sharedId: String) {
		viewer {
			actualPersonId
			component(name: "phase_relay_wrapper_v2")
			...PhaseRelayWrapperV2_viewer
				@arguments(searchQuery: $searchQuery, pageSize: $pageSize, reportId: $reportId, sharedId: $sharedId)
		}
	}
`;
export {PhaseRelayWrapperV2Query};

export default createRefetchContainer(
	PhaseRelayWrapperV2,
	{
		viewer: graphql`
			fragment PhaseRelayWrapperV2_viewer on Viewer
			@argumentDefinitions(
				searchQuery: {type: "TaskSearchQueryType"}
				pageSize: {type: "Int"}
				reportId: {type: "String"}
				sharedId: {type: "String"}
			) {
				reportData(id: $reportId, sharedId: $sharedId) {
					phases(first: $pageSize, searchQuery: $searchQuery)
						@connection(key: "TaskReport_phases", filters: ["searchQuery"]) {
						pageInfo {
							endCursor
							hasNextPage
						}
						edges {
							node {
								id
								phaseId
								name
								taskTotals {
									work {
										estimate {
											minutes
											points
										}
										remaining {
											minutes
											points
										}
									}
									reported {
										time
										points
										projected
									}
									financial {
										revenue {
											planned
											actual
										}
										billableActualMinutes
										nonBillableActualMinutes
										totalTimeAtCompletion
										billablePlannedTimeAndExpenses
										billableActualTimeAndExpenses
										billableForecastTimeAndExpensesToComplete
										billableTotalTimeAndExpensesAtCompletion
										totalForecastRevenueToComplete
										totalRevenueRecognition
										surplus
									}
									entities
								}
								noPhase
							}
						}
					}
				}
			}
		`,
	},
	graphql`
		query PhaseRelayWrapperV2RefetchQuery(
			$pageSize: Int
			$searchQuery: TaskSearchQueryType
			$reportId: String
			$sharedId: String
		) {
			viewer {
				...PhaseRelayWrapperV2_viewer
					@arguments(pageSize: $pageSize, searchQuery: $searchQuery, reportId: $reportId, sharedId: $sharedId)
			}
		}
	`
);
